import { ILocation } from './gymClusters';

export interface GeoLocation {
    latitude: number;
    longitude: number;
}

/**
 * Default fallback location
 * Example: Amsterdam
 */
const DEFAULT_LOCATION: GeoLocation = {
    latitude: 52.372435,
    longitude: 4.877478,
};

// Cache key for storing location in localStorage (if needed in future)
const LOCATION_CACHE_KEY = 'user_location';

const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours

let clientIpAddress: string = '';

export const getClientIpAddress = async (): Promise<string> => {
    if (clientIpAddress) {
        return clientIpAddress;
    }

    const res = await fetch('https://ipapi.co/json/');

    if (!res.ok) {
        throw new Error('Unable to retrieve client IP address');
    }

    const data = await res.json();

    if (typeof data.ip !== 'string' || !data.ip.trim()) {
        throw new Error('Invalid IP address returned');
    }

    clientIpAddress = data.ip;
    return clientIpAddress;
};

/**
 * Get location via IP (fallback)
 */
const getLocationFromIP = async (): Promise<GeoLocation> => {
    const cached = localStorage.getItem(LOCATION_CACHE_KEY);

    if (cached) {
        const parsed = JSON.parse(cached);

        if (Date.now() - parsed.timestamp < CACHE_TTL) {
            clientIpAddress = parsed.ip ?? null;
            return parsed.location;
        }
    }

    const res = await fetch('https://ipapi.co/json/');
    if (!res.ok) {
        throw new Error('IP API failed');
    }
    const data = await res.json();

    if (
        typeof data.latitude !== 'number' ||
        typeof data.longitude !== 'number'
    ) {
        throw new Error('Invalid location data');
    }

    const location = {
        latitude: data.latitude,
        longitude: data.longitude,
    };

    clientIpAddress = data.ip ?? null;

    localStorage.setItem(
        LOCATION_CACHE_KEY,
        JSON.stringify({
            location,
            timestamp: Date.now(),
        })
    );

    return location;
};

/**
 * Convert degrees → radians
 */
const toRad = (value: number): number => (value * Math.PI) / 180;

/**
 * Calculate distance between two coordinates (Haversine)
 */
const getDistance = (
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
): number => {
    const R = 6371; // Earth radius in KM

    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);

    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(lat1)) *
            Math.cos(toRad(lat2)) *
            Math.sin(dLon / 2) *
            Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
};

/**
 * Find nearest location from list
 */
export const findNearestLocation = (
    userLocation: GeoLocation,
    allLocations: ILocation[]
): ILocation | null => {
    if (!allLocations || !allLocations.length) return null;

    let nearest: ILocation | null = null;
    let minDistance = Infinity;

    for (const location of allLocations) {
        if (!location.latitude || !location.longitude) continue;

        const lat = parseFloat(location.latitude);
        const lon = parseFloat(location.longitude);

        if (isNaN(lat) || isNaN(lon)) continue;

        const distance = getDistance(
            userLocation.latitude,
            userLocation.longitude,
            lat,
            lon
        );

        if (distance < minDistance) {
            minDistance = distance;
            nearest = location;
        }
    }

    return nearest;
};

/**
 * Get user's current location (GPS)
 */
const getCurrentLocation = (): Promise<GeoLocation> => {
    return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
            reject({
                code: 0,
                message: 'Geolocation is not supported',
            });
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                resolve({
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                });
            },
            (error) => {
                reject({
                    code: error.code,
                    message: error.message,
                });
            },
            {
                enableHighAccuracy: true,
                timeout: 8000,
                maximumAge: 0,
            }
        );
    });
};

/**
 * Main function with fallback
 */
export const getUserLocation = async (): Promise<GeoLocation> => {
    try {
        // 1. Try IP location first (VPN aware)
        return await getLocationFromIP();
    } catch (ipError) {
        console.warn('IP location failed, trying GPS');

        try {
            // 2. Fallback to GPS
            return await getCurrentLocation();
        } catch (gpsError) {
            console.warn('GPS failed, using default location');

            // 3. Default fallback
            return DEFAULT_LOCATION;
        }
    }
};

export const getCityName = (_gym: ILocation): string | null => {
    if (_gym.address?.city && _gym.address.city.trim() !== "") {
        return _gym.address.city.trim();
    }

    if (_gym.location_name && typeof _gym.location_name === "string") {
        return _gym.location_name.split(" - ")[0].trim();
    }

    return null;
};

export const getNearByGymLocations = (
    selectedGym: ILocation,
    allGyms: ILocation[],
    radiusKm = 10
): ILocation[] => {
    if (!selectedGym || !allGyms.length) return [];

    const lat1 = parseFloat(selectedGym.latitude ?? "NaN");
    const lon1 = parseFloat(selectedGym.longitude ?? "NaN");
    if (isNaN(lat1) || isNaN(lon1)) return [];

    // Single pass: compute distance, filter, and collect
    const nearbyWithDistance: { gym: ILocation; distance: number }[] = [];

    for (const gym of allGyms) {
        if (gym.location_id === selectedGym.location_id) continue;

        const lat2 = parseFloat(gym.latitude ?? "NaN");
        const lon2 = parseFloat(gym.longitude ?? "NaN");
        // Skip gyms with invalid coordinates
        if (isNaN(lat2) || isNaN(lon2)) continue;

        const distance = getDistance(lat1, lon1, lat2, lon2);
        if (distance <= radiusKm) {
            nearbyWithDistance.push({ gym, distance });
        }
    }

  // Sort once by distance
  nearbyWithDistance.sort((a, b) => a.distance - b.distance);

  // Return only gyms
  return nearbyWithDistance.map(({ gym }) => gym);
};
